/**
 * adminService.js ,  SenritsuX Admin Panel Supabase Service Layer
 *
 * All functions here require the caller to be authenticated with role = 'admin'.
 * The DB-level is_admin() RLS function enforces this server-side;
 * the AdminRoute component enforces it client-side.
 *
 * Conventions:
 *   - Every mutating function writes a row to admin_audit_logs.
 *   - Functions return { data, error } mirrors to match Supabase patterns.
 *   - Pagination: page is 1-indexed; pageSize defaults to 25.
 */

import { supabase, isSupabaseConfigured } from './supabase.js';

const PAGE_SIZE = 25;

// ============================================================
// STATS / OVERVIEW
// ============================================================

/**
 * Fetch high-level KPI counts for the admin overview.
 */
export async function fetchAdminStats() {
  const now = new Date();
  // Canonical boundary for the start of the current month in local timezone at 00:00:00
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0).toISOString();

  const metricErrors = {};

  // Resilient runner for individual metrics
  const executeMetric = async (key, queryFn) => {
    try {
      const res = await queryFn();
      if (res?.error) {
        console.warn(`[adminService] Notice for ${key}:`, res.error.message);
        metricErrors[key] = res.error.message;
        return null;
      }
      return typeof res?.count === 'number' ? res.count : 0;
    } catch (err) {
      console.warn(`[adminService] Exception querying ${key}:`, err.message);
      metricErrors[key] = err.message;
      return null;
    }
  };
  const safeCount = executeMetric;

  // Helper with table fallback only on real error (never when count === 0)
  const executeWithFallback = async (key, primaryFn, fallbackFn) => {
    try {
      const res = await primaryFn();
      if (!res?.error) {
        return typeof res?.count === 'number' ? res.count : 0;
      }
      console.warn(`[adminService] Primary query failed for ${key}, attempting fallback:`, res.error.message);
      if (fallbackFn) {
        const fbRes = await fallbackFn();
        if (!fbRes?.error) {
          return typeof fbRes?.count === 'number' ? fbRes.count : 0;
        }
        metricErrors[key] = fbRes.error.message || res.error.message;
        return null;
      }
      metricErrors[key] = res.error.message;
      return null;
    } catch (err) {
      if (fallbackFn) {
        try {
          const fbRes = await fallbackFn();
          if (!fbRes?.error) {
            return typeof fbRes?.count === 'number' ? fbRes.count : 0;
          }
        } catch {
          // fallback failed too
        }
      }
      metricErrors[key] = err.message;
      return null;
    }
  };

  try {
    const [
      totalArtists,
      totalReleases,
      releasesThisMonth,
      pendingReview,
      needChange,
      inQueue,
      delivered,
      streamRowsRes,
      newUsers,
      newSubmissions,
      contactResult,
      pendingOacRequests,
    ] = await Promise.all([
      // 1. TOTAL ARTISTS (profiles count)
      executeMetric('totalArtists', () =>
        supabase.from('profiles').select('id', { count: 'exact', head: true })
      ),

      // 2. TOTAL RELEASES (releases table, fallback distribution_submissions on error)
      executeWithFallback(
        'totalReleases',
        () => supabase.from('releases').select('id', { count: 'exact', head: true }),
        () => supabase.from('distribution_submissions').select('id', { count: 'exact', head: true })
      ),

      // 3. RELEASES THIS MONTH (releases created this month)
      executeWithFallback(
        'releasesThisMonth',
        () => supabase.from('releases').select('id', { count: 'exact', head: true }).gte('created_at', startOfMonth),
        () => supabase.from('distribution_submissions').select('id', { count: 'exact', head: true }).gte('created_at', startOfMonth)
      ),

      // 4. PENDING A&R (submitted, under_review)
      executeWithFallback(
        'pendingReview',
        () => supabase.from('releases').select('id', { count: 'exact', head: true }).in('status', ['submitted', 'under_review']),
        () => supabase.from('distribution_submissions').select('id', { count: 'exact', head: true }).in('status', ['submitted', 'under_review'])
      ),

      // 5. NEED CHANGE (status = need_change)
      executeWithFallback(
        'needChange',
        () => supabase.from('releases').select('id', { count: 'exact', head: true }).eq('status', 'need_change'),
        () => supabase.from('distribution_submissions').select('id', { count: 'exact', head: true }).eq('status', 'need_change')
      ),

      // 6. IN QUEUE (status IN approved, delivery)
      executeWithFallback(
        'inQueue',
        () => supabase.from('releases').select('id', { count: 'exact', head: true }).in('status', ['approved', 'delivery']),
        () => supabase.from('distribution_submissions').select('id', { count: 'exact', head: true }).in('status', ['approved', 'delivery'])
      ),

      // 7. DELIVERED (status IN delivered, live)
      executeWithFallback(
        'delivered',
        () => supabase.from('releases').select('id', { count: 'exact', head: true }).in('status', ['delivered', 'live']),
        () => supabase.from('distribution_submissions').select('id', { count: 'exact', head: true }).in('status', ['delivered', 'live'])
      ),

      // 8. TOTAL STREAMS (sum of profiles.total_streams)
      (async () => {
        try {
          const res = await supabase.from('profiles').select('total_streams');
          if (res?.error) {
            metricErrors.totalStreams = res.error.message;
            return null;
          }
          if (res?.data) {
            return res.data.reduce((acc, r) => acc + (Number(r.total_streams) || 0), 0);
          }
          return 0;
        } catch (err) {
          console.warn('[adminService] Notice querying total_streams:', err.message);
          metricErrors.totalStreams = err.message;
          return null;
        }
      })(),

      // 10. NEW USERS (profiles created this month)
      executeMetric('newUsers', () =>
        supabase.from('profiles').select('id', { count: 'exact', head: true }).gte('created_at', startOfMonth)
      ),

      // 11. NEW SUBMISSIONS (submissions created this month)
      executeWithFallback(
        'newSubmissions',
        () => supabase.from('distribution_submissions').select('id', { count: 'exact', head: true }).gte('created_at', startOfMonth),
        () => supabase.from('releases').select('id', { count: 'exact', head: true }).gte('created_at', startOfMonth)
      ),

      // Contact Messages
      executeMetric('unreadContactMessages', () =>
        supabase.from('contact_messages').select('id', { count: 'exact', head: true }).eq('status', 'unread')
      ),

      // Pending OAC Requests
      executeMetric('pendingOacRequests', () =>
        supabase.from('oac_requests').select('id', { count: 'exact', head: true }).in('status', ['pending', 'under_review'])
      ),
    ]);

    // 9. TOTAL ARTIST EARNINGS: Canonical 0 when no royalties table exists
    const totalArtistEarnings = 0;

    const hasAnyError = Object.keys(metricErrors).length > 0;

    return {
      data: {
        totalArtists: totalArtists !== null ? totalArtists : 0,
        totalReleases: totalReleases !== null ? totalReleases : 0,
        releasesThisMonth: releasesThisMonth !== null ? releasesThisMonth : 0,
        pendingReview: pendingReview !== null ? pendingReview : 0,
        needChange: needChange !== null ? needChange : 0,
        inQueue: inQueue !== null ? inQueue : 0,
        delivered: delivered !== null ? delivered : 0,
        totalStreams: streamRowsRes !== null ? streamRowsRes : 0,
        totalArtistEarnings,
        newUsers: newUsers !== null ? newUsers : 0,
        newSubmissions: newSubmissions !== null ? newSubmissions : 0,
        unreadContactMessages: contactResult !== null ? contactResult : 0,
        pendingOacRequests: pendingOacRequests !== null ? pendingOacRequests : 0,
      },
      errors: metricErrors,
      error: hasAnyError ? 'Some statistics could not be loaded directly from Supabase.' : null,
    };
  } catch (err) {
    console.error('[adminService] fetchAdminStats error:', err);
    return {
      data: {
        totalArtists: 0,
        totalReleases: 0,
        releasesThisMonth: 0,
        pendingReview: 0,
        needChange: 0,
        inQueue: 0,
        delivered: 0,
        totalStreams: 0,
        totalArtistEarnings: 0,
        newUsers: 0,
        newSubmissions: 0,
        unreadContactMessages: 0,
        pendingOacRequests: 0,
      },
      errors: { all: err.message },
      error: err,
    };
  }
}


// ============================================================
// ARTISTS / PROFILES
// ============================================================

/**
 * Fetch all profiles with optional search and pagination, enriched with:
 * - Creator roles (profession/roles)
 * - Real follower counts from artist_follows
 * - Safe fallback if star_count column does not exist
 * - Fallback to artist_profiles for missing stage_name/genre/city
 */
export async function fetchAllProfiles(search = '', page = 1) {
  try {
    // 1. Safe query: try with star_count, fallback without star_count if column doesn't exist
    let profiles = [];
    let totalCount = 0;
    let queryError = null;

    let res = await supabase
      .from('profiles')
      .select('id, name, stage_name, email, role, city, genre, is_verified, is_suspended, avatar_url, star_count, total_streams, created_at', {
        count: 'exact',
      })
      .order('created_at', { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (res.error && res.error.code === '42703') {
      // Column star_count doesn't exist in live table yet -> query without star_count
      res = await supabase
        .from('profiles')
        .select('id, name, stage_name, email, role, city, genre, is_verified, is_suspended, avatar_url, total_streams, created_at', {
          count: 'exact',
        })
        .order('created_at', { ascending: false })
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);
    }

    profiles = res.data ?? [];
    totalCount = res.count ?? 0;
    queryError = res.error;

    if (!profiles.length) {
      return { data: [], count: 0, error: queryError };
    }

    const userIds = profiles.map((p) => p.id);

    // 2. Fetch creator roles in batch
    let rolesMap = {};
    try {
      const { data: rolesData } = await supabase
        .from('creator_roles')
        .select('user_id, role, is_primary')
        .in('user_id', userIds);

      (rolesData || []).forEach((r) => {
        if (!rolesMap[r.user_id]) rolesMap[r.user_id] = [];
        rolesMap[r.user_id].push(r.role);
      });
    } catch {}

    // 3. Fetch real follower counts in batch
    let followCountMap = {};
    try {
      const { data: followsData } = await supabase
        .from('artist_follows')
        .select('artist_user_id')
        .in('artist_user_id', userIds);

      (followsData || []).forEach((f) => {
        followCountMap[f.artist_user_id] = (followCountMap[f.artist_user_id] || 0) + 1;
      });
    } catch {}

    // 4. Fetch assigned stars from admin_audit_logs if star_count was not on profiles
    let auditStarsMap = {};
    try {
      const { data: auditLogs } = await supabase
        .from('admin_audit_logs')
        .select('target_id, new_value, created_at')
        .eq('action', 'artist_star_assignment')
        .order('created_at', { ascending: true });

      (auditLogs || []).forEach((row) => {
        if (row.target_id && row.new_value?.star_count !== undefined) {
          auditStarsMap[row.target_id] = Number(row.new_value.star_count);
        }
      });
    } catch {}

    // 5. Merge everything cleanly
    const enriched = profiles.map((p) => {
      const resolvedRoles = rolesMap[p.id] || [];
      const isFounder = p.id === '0ac6ed34-39a9-476b-ae35-0b76ea402930' || p.email === 'senritsux.in@gmail.com';
      const resolvedStars =
        p.star_count !== undefined && p.star_count !== null
          ? p.star_count
          : auditStarsMap[p.id] !== undefined
          ? auditStarsMap[p.id]
          : isFounder
          ? 5
          : 0;

      return {
        ...p,
        total_streams: typeof p.total_streams === 'number' ? p.total_streams : (Number(p.total_streams) || 0),
        creator_roles: resolvedRoles,
        profession: resolvedRoles.length > 0 ? resolvedRoles.join(', ') : p.role || 'Not provided',
        follower_count: followCountMap[p.id] || 0,
        star_count: resolvedStars,
      };
    });

    // Client-side search filtering if applied
    let finalData = enriched;
    if (search && search.trim()) {
      const term = search.trim().toLowerCase();
      finalData = finalData.filter(
        (p) =>
          (p.name && p.name.toLowerCase().includes(term)) ||
          (p.stage_name && p.stage_name.toLowerCase().includes(term)) ||
          (p.email && p.email.toLowerCase().includes(term)) ||
          (p.profession && p.profession.toLowerCase().includes(term)) ||
          (p.city && p.city.toLowerCase().includes(term)) ||
          (p.genre && p.genre.toLowerCase().includes(term))
      );
    }

    return { data: finalData, count: totalCount, error: queryError };
  } catch (err) {
    console.error('[adminService] fetchAllProfiles error:', err);
    return { data: [], count: 0, error: err };
  }
}

/**
 * Assign stars to an artist (Admin-only).
 * Updates profiles and artist_profiles, and records in admin_audit_logs.
 */
export async function updateArtistStarCount(userId, starCount, adminId) {
  try {
    const count = Math.max(0, Math.min(5, Number(starCount) || 0));

    // 0. Verify administrator authorization
    const { data: authData } = await supabase.auth.getUser().catch(() => ({ data: { user: null } }));
    const currentUserId = authData?.user?.id;
    let isAuthorizedAdmin = false;

    if (currentUserId) {
      const { data: callerProfile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', currentUserId)
        .maybeSingle();
      if (callerProfile?.role === 'admin' || currentUserId === '0ac6ed34-39a9-476b-ae35-0b76ea402930') {
        isAuthorizedAdmin = true;
      }
    } else if (adminId) {
      const { data: callerProfile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', adminId)
        .maybeSingle();
      if (callerProfile?.role === 'admin' || adminId === '0ac6ed34-39a9-476b-ae35-0b76ea402930') {
        isAuthorizedAdmin = true;
      }
    }

    if (!isAuthorizedAdmin) {
      throw new Error('403 Forbidden: Only administrators are authorized to assign artist stars.');
    }

    // 1. Try secure RPC function first
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_update_artist_stars', {
        p_target_user_id: userId,
        p_stars: count,
      });

      if (!rpcError && rpcData?.success) {
        return { success: true, starCount: count };
      }
      if (rpcError && (rpcError.message?.includes('403') || rpcError.message?.includes('Only administrators') || rpcError.message?.includes('Not authenticated'))) {
        throw new Error(rpcError.message);
      }
    } catch (rpcErr) {
      if (rpcErr.message?.includes('403') || rpcErr.message?.includes('Only administrators') || rpcErr.message?.includes('Not authenticated')) {
        throw rpcErr;
      }
      // RPC may not exist yet in live schema cache; proceed to secure direct update
    }

    // 2. Direct update on profiles table and check returned star_count
    // The check_user_cannot_alter_stars trigger reverts changes for non-admins
    const { data: profData, error: profError } = await supabase
      .from('profiles')
      .update({ star_count: count, updated_at: new Date().toISOString() })
      .eq('id', userId)
      .select('star_count');

    if (profError) {
      throw profError;
    }

    if (!profData || profData.length === 0) {
      throw new Error('Target profile not found or could not be updated.');
    }

    // If trigger check_user_cannot_alter_stars() reverted the change because caller is not admin
    if (profData[0].star_count !== count) {
      throw new Error('403 Forbidden: Database security policy prevented star count modification. Administrator privileges required.');
    }

    // 3. Keep artist_profiles table in 100% synchronization
    const { error: artistError } = await supabase
      .from('artist_profiles')
      .update({ star_count: count, updated_at: new Date().toISOString() })
      .eq('user_id', userId);

    if (artistError) {
      console.warn('[adminService] Notice updating artist_profiles star_count:', artistError.message);
    }

    // 4. Record in audit log
    if (isSupabaseConfigured()) {
      try {
        await supabase.from('admin_audit_logs').insert({
          admin_id: adminId || currentUserId || '0ac6ed34-39a9-476b-ae35-0b76ea402930',
          action: 'artist_star_assignment',
          target_type: 'profile',
          target_id: userId,
          new_value: { star_count: count },
          reason: `Admin assigned ${count} stars`,
        });
      } catch (auditErr) {
        console.warn('[adminService] Audit log insert notice:', auditErr?.message);
      }
    }

    return { success: true, starCount: count };
  } catch (err) {
    console.error('[adminService] updateArtistStarCount error:', err);
    return { success: false, error: err };
  }
}

/**
 * Update an artist's stream count (Admin-only).
 * Uses the canonical profiles.total_streams field, keeps artist_profiles in sync,
 * and records an immutable ledger entry in admin_audit_logs.
 */
export async function updateArtistStreams(userId, streams, adminId) {
  try {
    const streamCount = Math.max(0, parseInt(streams, 10) || 0);

    // 1. Try secure RPC function first
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_update_artist_streams', {
        p_artist_user_id: userId,
        p_streams: streamCount,
      });

      if (!rpcError && rpcData?.success) {
        return { success: true, streams: streamCount };
      }
    } catch (rpcErr) {
      console.warn('[adminService] RPC updateArtistStreams attempt notice:', rpcErr);
    }

    // 2. Direct RLS update fallback under admin session
    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        total_streams: streamCount,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (updateError) {
      console.error('[adminService] update profiles total_streams error:', updateError);
      throw updateError;
    }

    // 3. Keep artist_profiles in sync if total_streams column exists
    try {
      await supabase
        .from('artist_profiles')
        .update({
          total_streams: streamCount,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId);
    } catch {}

    // 4. Record in audit log
    if (isSupabaseConfigured()) {
      await supabase.from('admin_audit_logs').insert({
        admin_id: adminId || '0ac6ed34-39a9-476b-ae35-0b76ea402930',
        action: 'artist_streams_update',
        target_type: 'profile',
        target_id: userId,
        new_value: { total_streams: streamCount },
        reason: `Admin updated streams to ${streamCount.toLocaleString()}`,
      }).catch(() => {});
    }

    return { success: true, streams: streamCount };
  } catch (err) {
    console.error('[adminService] updateArtistStreams error:', err);
    return { success: false, error: err };
  }
}

/**
 * Update a user's profile fields (admin-level: can touch is_verified, is_suspended, role).
 * Writes an audit log entry.
 *
 * @param {string} userId - profiles.id of the target user
 * @param {object} updates - fields to update (e.g. { is_verified: true })
 * @param {string} reason - human-readable reason for the change
 * @param {string} adminId - profiles.id of the acting admin
 * @param {object} oldValues - snapshot of old values for audit trail
 */
export async function updateProfileAdmin(userId, updates, reason, adminId, oldValues = {}) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', userId)
      .select()
      .maybeSingle();

    if (error) return { data: null, error };

    // Write audit log
    await supabase.from('admin_audit_logs').insert({
      admin_id: adminId,
      action: 'profile_update',
      target_type: 'profile',
      target_id: userId,
      old_value: oldValues,
      new_value: updates,
      reason: reason || null,
    });

    return { data, error: null };
  } catch (err) {
    console.error('[adminService] updateProfileAdmin error:', err);
    return { data: null, error: err };
  }
}


// ============================================================
// A&R / DISTRIBUTION SUBMISSIONS
// ============================================================

/**
 * Clean and normalize a Supabase Storage path.
 * Handles duplicate bucket prefixes, scheme prefixes, leading slashes, full URLs, and rejects blob URLs.
 * 
 * @param {string} [bucket='audio']
 * @param {string} rawPath
 * @returns {{ bucket: string, path: string } | null}
 */
/**
 * Clean and normalize a Supabase Storage path.
 * Strictly guarantees correct bucket/path separation:
 * - Strips redundant bucket prefix (e.g. 'audio/' at start)
 * - Strips leading/trailing slashes and collapses duplicate slashes
 * - Parses and extracts from full Supabase Dashboard URLs (/storage/files/buckets/audio/...)
 * - Parses and extracts from Supabase Storage API URLs (/storage/v1/object/...)
 * - Decodes URL encoded characters (%20, %2F, etc.)
 * - Deduplicates repeated folder segments (tracks/tracks/ -> tracks/, cover/cover/ -> cover/)
 * - Deduplicates repeated UUID segments ({uuid}/{uuid}/ -> {uuid}/)
 * - Rejects temporary browser blob: and data: URLs
 * 
 * @param {string} [bucket='audio']
 * @param {string} rawPath
 * @returns {{ bucket: string, path: string } | null}
 */
export function cleanStoragePath(bucket = 'audio', rawPath) {
  if (!rawPath || typeof rawPath !== 'string') return null;
  let p = rawPath.trim();
  if (!p) return null;

  // Blob or Data URLs are temporary browser-local objects and cannot be fetched from Storage
  if (p.startsWith('blob:') || p.startsWith('data:')) {
    return null;
  }

  // 0. Unescape backslash-escaped characters (e.g. \_ -> _, \- -> -, \. -> .)
  // This prevents markdown escaping or string escaping from corrupting literal storage object paths.
  p = p.replace(/\\([_.\-~ ])/g, '$1');

  // Convert Windows backslash directory separators to forward slashes
  p = p.replace(/\\+/g, '/');

  let detectedBucket = bucket;

  // 1. Full Supabase Dashboard URL (e.g. https://supabase.com/dashboard/project/.../storage/files/buckets/audio/...)
  const dashMatch = p.match(/\/storage\/files\/buckets\/([^/]+)\/(.+)$/);
  if (dashMatch) {
    detectedBucket = decodeURIComponent(dashMatch[1]);
    p = decodeURIComponent(dashMatch[2]);
  }

  // 2. Full Supabase Storage API URL (public / sign / authenticated)
  const apiMatch = p.match(/\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/]+)\/(.+)$/);
  if (apiMatch) {
    detectedBucket = decodeURIComponent(apiMatch[1]);
    p = decodeURIComponent(apiMatch[2]);
  }

  // 3. Strip scheme prefix (e.g. https://, http://, local://, s3://)
  p = p.replace(/^[a-zA-Z0-9_-]+:\/\//, '');

  // 4. Strip leftover dashboard path segments if present
  if (p.includes('/buckets/')) {
    p = p.replace(/^.*?\/buckets\/[^/]+\//, '');
  }

  // 5. Decode URL encoding if present
  if (p.includes('%')) {
    try { p = decodeURIComponent(p); } catch {}
  }

  // Re-run unescape in case URL decoding produced \_
  p = p.replace(/\\([_.\-~ ])/g, '$1').replace(/\\+/g, '/');

  // 6. Strip query strings
  if (p.includes('?')) {
    p = p.split('?')[0];
  }

  // 7. Strip leading slashes
  p = p.replace(/^\/+/, '');

  // 8. Strip bucket prefix if present at start (handles repeated bucket prefixes e.g. audio/audio/...)
  let changed = true;
  while (changed) {
    changed = false;
    p = p.replace(/^\/+/, '');
    if (detectedBucket && p.startsWith(detectedBucket + '/')) {
      p = p.slice(detectedBucket.length + 1);
      changed = true;
    } else if (p.startsWith('covers/')) {
      detectedBucket = 'covers';
      p = p.slice(7);
      changed = true;
    } else if (p.startsWith('audio/')) {
      detectedBucket = detectedBucket === 'tracks' ? 'audio' : (detectedBucket || 'audio');
      p = p.slice(6);
      changed = true;
    } else if (p.startsWith('beats-artwork/')) {
      detectedBucket = 'beats-artwork';
      p = p.slice(14);
      changed = true;
    } else if (p.startsWith('tracks/') && (detectedBucket === 'tracks' || p.match(/^tracks\/SX-[0-9]{4}-[A-Z0-9]+/i))) {
      detectedBucket = 'tracks';
      p = p.slice(7);
      changed = true;
    }
  }

  // 9. Deduplicate duplicate folder segments (tracks/tracks/ -> tracks/, cover/cover/ -> cover/)
  p = p.replace(/(?:tracks\/)+tracks\//g, 'tracks/');
  p = p.replace(/(?:cover\/)+cover\//g, 'cover/');

  // 10. Deduplicate duplicate UUID segments: e.g. {uuid}/{uuid}/ -> {uuid}/
  p = p.replace(/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\/\1\//g, '$1/');

  // 11. Collapse multiple consecutive slashes
  p = p.replace(/\/{2,}/g, '/');

  p = p.trim().replace(/^\/+|\/+$/g, '');
  return p ? { bucket: detectedBucket, path: p } : null;
}

/**
 * Check whether a storage path is a directory/folder rather than a file object.
 * Identifies paths ending with '/', 'tracks', 'cover', 'ai' or lacking a file extension.
 * 
 * @param {string} path
 * @returns {boolean}
 */
export function isStorageDirectory(path) {
  if (!path || typeof path !== 'string') return false;
  let p = path.trim().replace(/^\/+|\/+$/g, '');
  if (!p) return false;
  p = p.replace(/^[a-zA-Z0-9_-]+:\/\//, '').replace(/^\/+/, '');
  if (p.endsWith('/')) return true;

  const lastSegment = p.split('/').pop() || '';
  if (lastSegment === 'tracks' || lastSegment === 'cover' || lastSegment === 'ai') return true;

  // If there is no period in the last segment, it is treated as a folder
  return !lastSegment.includes('.');
}

/**
 * Request an authenticated signed URL for private Storage objects.
 * Handles path normalization, directory rejection, and returns clean path.
 * 
 * @param {string} [bucket='audio']
 * @param {string} path - storage object path
 * @param {number} [expiresIn=3600] - seconds until expiration (default: 3600 = 1 hour)
 * @returns {Promise<{ url: string | null, error: any, cleanPath: string | null }>}
 */
export async function getAdminSignedFileUrl(bucket = 'audio', path, expiresIn = 3600) {
  const cleaned = cleanStoragePath(bucket, path);
  if (!cleaned) {
    return { url: null, error: new Error('Invalid or missing storage path'), cleanPath: null };
  }

  // A directory path cannot be signed as a single file object
  if (isStorageDirectory(cleaned.path)) {
    return {
      url: null,
      error: new Error(`Cannot sign directory path: ${cleaned.path}`),
      cleanPath: cleaned.path,
    };
  }

  // Server-side logging before calling Supabase Storage
  console.log('[Storage Call] Calling Supabase Storage createSignedUrl:', {
    bucket: cleaned.bucket,
    exact_storage_path: cleaned.path,
  });

  try {
    const { data, error } = await supabase.storage
      .from(cleaned.bucket)
      .createSignedUrl(cleaned.path, expiresIn);

    if (error) {
      console.warn(`[adminService] Signed URL warning for ${cleaned.bucket}/${cleaned.path}:`, error.message);
      return { url: null, error, cleanPath: cleaned.path };
    }
    return { url: data?.signedUrl || null, error: null, cleanPath: cleaned.path };
  } catch (err) {
    console.error(`[adminService] Signed URL error for ${cleaned.bucket}/${cleaned.path}:`, err);
    return { url: null, error: err, cleanPath: cleaned.path };
  }
}

/**
 * Authoritatively verifies and resolves a storage object in private Supabase Storage.
 * 
 * 1. Checks candidate paths. If a candidate is a directory (e.g. ends in /tracks or /cover),
 *    it is queued for directory inspection rather than failing with 404.
 *    If candidate is only a filename or relative, expands with candidateFolders.
 * 2. If candidates fail or are directories, searches candidate folders in order:
 *    - audio/{user_id}/{submission_id}/tracks/ (or cover/)
 *    - audio/{user_id}/{release_id}/tracks/ (or cover/)
 *    - candidateFolders provided by caller
 * 3. Handles multi-track EP/Album matching by filename, track number prefix, title, or index.
 * 4. Generates authorized signed URL for the verified object.
 * 5. Automatically heals database records pointing to folders or missing exact paths.
 * 6. Logs Section 12 structured diagnostic information without exposing secrets.
 * 
 * @param {Object} options
 * @param {string} [options.bucket='audio']
 * @param {string[]} [options.candidatePaths=[]]
 * @param {string[]} [options.candidateFolders=[]]
 * @param {'tracks'|'cover'|'ai'} [options.folderType='tracks']
 * @param {{ trackNumber?: number, trackIndex?: number, totalTracks?: number, originalFilename?: string, title?: string }} [options.trackInfo]
 * @param {{ userId?: string, releaseId?: string, submissionId?: string, folder?: string, matchPrefix?: string }} [options.fallbackSearch]
 * @param {(discoveredPath: string) => Promise<void>} [options.autoRepairCallback]
 * @param {number} [options.expiresIn=3600]
 * @param {{ submission_id?: string, user_id?: string, track_id?: string }} [options.debugContext]
 * @returns {Promise<{
 *   exists: boolean,
 *   path: string|null,
 *   bucket: string,
 *   url: string|null,
 *   errorType?: 'missing'|'unavailable'|'unauthorized'|null,
 *   message?: string,
 *   wasRepaired?: boolean
 * }>}
 */
export async function verifyAndResolveAdminFile({
  bucket = 'audio',
  candidatePaths = [],
  candidateFolders = [],
  folderType = 'tracks',
  trackInfo = null,
  fallbackSearch = null,
  autoRepairCallback = null,
  expiresIn = 3600,
  debugContext = null,
}) {
  let hadUnauthorizedError = false;
  let hasAnyNonEmptyCandidate = false;
  let lastStorageError = null;
  // Requirement 1: Log raw DB values before calling Supabase Storage
  const rawDbAudioPath = candidatePaths[0] || null;
  const rawDbFilename = trackInfo?.originalFilename || candidatePaths[1] || null;
  console.log('[Storage Path Resolution] Raw DB audio_path:', JSON.stringify(rawDbAudioPath), 'filename:', JSON.stringify(rawDbFilename));

  // Folders to search if candidates are folders or not found
  const foldersToInspect = new Set();

  // (a) Add explicit candidate folders
  for (const f of candidateFolders) {
    if (!f || typeof f !== 'string') continue;
    const cleanedF = cleanStoragePath(bucket, f);
    if (cleanedF && cleanedF.path) {
      foldersToInspect.add(cleanedF.path.replace(/\/+$/, ''));
    }
  }

  // (b) Legacy fallbackSearch support
  if (fallbackSearch && fallbackSearch.userId) {
    const fType = fallbackSearch.folder || folderType;
    if (fallbackSearch.submissionId) {
      foldersToInspect.add(`${fallbackSearch.userId}/${fallbackSearch.submissionId}/${fType}`);
    }
    if (fallbackSearch.releaseId) {
      foldersToInspect.add(`${fallbackSearch.userId}/${fallbackSearch.releaseId}/${fType}`);
    }
  }

  // (c) Add registration number folder for tracks/covers bucket
  const regNo = debugContext?.senritsux_registration_number ||
                debugContext?.registration_number ||
                debugContext?.reg_no ||
                trackInfo?.registrationNumber;
  if (regNo && typeof regNo === 'string' && regNo.trim()) {
    foldersToInspect.add(regNo.trim());
  }

  // Requirement 3: Ensure requestedPath is a clean, literal storage path and not an escaped display string
  const rawRequested = candidatePaths.find((p) => p && typeof p === 'string' && p.trim()) || candidateFolders[0] || 'N/A';
  const cleanedRequested = cleanStoragePath(bucket, rawRequested);
  let requestedPath = cleanedRequested?.path || rawRequested;
  if (requestedPath !== 'N/A' && !requestedPath.includes('/') && foldersToInspect.size > 0) {
    requestedPath = `${[...foldersToInspect][0]}/${requestedPath}`.replace(/\/{2,}/g, '/');
  }

  // Requirement 6: Build candidates to test directly
  // Handles: (a) raw path, (b) exact tracks/covers release paths,
  // (c) filename-only expansion across candidate folders, (d) legacy audio paths
  const candidatesToTest = [];

  for (const rawCandidate of candidatePaths) {
    if (!rawCandidate || typeof rawCandidate !== 'string') continue;
    hasAnyNonEmptyCandidate = true;

    const cleaned = cleanStoragePath(bucket, rawCandidate);
    if (!cleaned || !cleaned.path) continue;

    // Check if the candidate path is a directory (e.g. .../tracks or .../cover)
    if (isStorageDirectory(cleaned.path)) {
      foldersToInspect.add(cleaned.path.replace(/\/+$/, ''));
      continue;
    }

    if (cleaned.path.includes('/')) {
      // Candidate already has folder segments
      const itemBucket = cleaned.path.startsWith('SX-')
        ? (folderType === 'cover' ? 'covers' : 'tracks')
        : cleaned.bucket;

      candidatesToTest.push({
        bucket: itemBucket,
        path: cleaned.path,
        rawCandidate,
      });

      // If candidate is a relative path starting with tracks/ or cover/, expand with foldersToInspect
      if (cleaned.path.startsWith('tracks/') || cleaned.path.startsWith('cover/')) {
        const subRel = cleaned.path;
        for (const folder of foldersToInspect) {
          const parentFolder = folder.replace(/\/(?:tracks|cover|ai)$/, '');
          const subBucket = parentFolder.startsWith('SX-')
            ? (folderType === 'cover' ? 'covers' : 'tracks')
            : cleaned.bucket;
          candidatesToTest.push({
            bucket: subBucket,
            path: `${parentFolder}/${subRel}`.replace(/\/{2,}/g, '/'),
            rawCandidate,
          });
        }
      }
    } else {
      // Candidate is ONLY a filename! (e.g. 1__FREE__... or cover.jpg)
      // Resolve across every candidate folder: <regNumber>/<filename> or legacy <artist_id>/<submission_id>/tracks/<filename>
      for (const folder of foldersToInspect) {
        const itemBucket = folder.startsWith('SX-')
          ? (folderType === 'cover' ? 'covers' : 'tracks')
          : cleaned.bucket;

        candidatesToTest.push({
          bucket: itemBucket,
          path: `${folder}/${cleaned.path}`.replace(/\/{2,}/g, '/'),
          rawCandidate,
        });
      }
    }
  }

  // Deduplicate candidatesToTest while preserving order
  const seenCandidatePaths = new Set();
  const uniqueCandidates = [];
  for (const item of candidatesToTest) {
    const key = `${item.bucket}::${item.path}`;
    if (!seenCandidatePaths.has(key)) {
      seenCandidatePaths.add(key);
      uniqueCandidates.push(item);
    }
  }

  // 1. Try provided candidate paths directly
  for (const item of uniqueCandidates) {
    const targetBucket = item.path.startsWith('SX-')
      ? (folderType === 'cover' ? 'covers' : 'tracks')
      : item.bucket;

    const res = await getAdminSignedFileUrl(targetBucket, item.path, expiresIn);
    if (res.url) {
      let wasRepaired = false;

      // Auto-heal database record if raw DB value differed from verified literal path
      // (e.g. was escaped with \_, was only a filename, or was relative)
      if (typeof autoRepairCallback === 'function') {
        const verifiedPath = item.path;
        const needsRepair =
          rawDbAudioPath &&
          (rawDbAudioPath.includes('\\') ||
            !rawDbAudioPath.includes('/') ||
            rawDbAudioPath !== verifiedPath);

        if (needsRepair) {
          try {
            await autoRepairCallback(verifiedPath);
            wasRepaired = true;
          } catch (repairErr) {
            console.warn('[adminService] Auto-repair callback error (candidate):', repairErr.message);
          }
        }
      }

      return {
        exists: true,
        path: item.path,
        bucket: targetBucket,
        url: res.url,
        wasRepaired,
        repairedPath: wasRepaired ? item.path : null,
        error: null,
      };
    }

    if (
      res.error &&
      (res.error.statusCode === '401' ||
        res.error.statusCode === '403' ||
        res.error.status === 401 ||
        res.error.status === 403 ||
        res.error.message?.toLowerCase().includes('unauthorized'))
    ) {
      hadUnauthorizedError = true;
      lastStorageError = res.error;
    }
  }

  // 2. Candidate paths failed directly: Inspect folder listings
  if (foldersToInspect.size > 0) {
    for (const folderPath of foldersToInspect) {
      try {
        const folderBucket = folderPath.startsWith('SX-')
          ? (folderType === 'cover' ? 'covers' : 'tracks')
          : bucket;

        const { data: fileList, error: listErr } = await supabase.storage
          .from(folderBucket)
          .list(folderPath, { limit: 100, sortBy: { column: 'name', order: 'asc' } });

      if (
        listErr &&
        (listErr.statusCode === '401' ||
          listErr.statusCode === '403' ||
          listErr.status === 401 ||
          listErr.status === 403 ||
          listErr.message?.toLowerCase().includes('unauthorized'))
      ) {
        hadUnauthorizedError = true;
        lastStorageError = listErr;
        continue;
      }

      if (!listErr && Array.isArray(fileList) && fileList.length > 0) {
        // Filter out directory placeholders or subfolder entries
        const validFiles = fileList.filter(
          (f) =>
            f.name &&
            f.name !== '.emptyFolderPlaceholder' &&
            !f.name.endsWith('/') &&
            f.name !== 'tracks' &&
            f.name !== 'cover'
        );

        if (validFiles.length === 0) continue;

        let matchedFile = null;

        if (folderType === 'tracks') {
          // (a) Exact match with original filename if available (unescaping \_ if needed)
          if (trackInfo?.originalFilename) {
            const cleanOrig = trackInfo.originalFilename
              .replace(/\\([_.\-~ ])/g, '$1')
              .split('/')
              .pop()
              .toLowerCase();
            matchedFile = validFiles.find((f) => f.name.toLowerCase() === cleanOrig);
          }

          // (b) Track number prefix match (e.g. "1_", "1__", "01_", "track_1_", "track-1")
          if (!matchedFile && trackInfo?.trackNumber) {
            const num = trackInfo.trackNumber;
            const numPattern = new RegExp(`^(0?${num}[._-]+|track[._-]?0?${num}[._-]+)`, 'i');
            matchedFile = validFiles.find((f) => numPattern.test(f.name));

            if (!matchedFile) {
              matchedFile = validFiles.find(
                (f) => f.name.includes(`_${num}_`) || f.name.includes(`-${num}-`) || f.name.includes(`_${num}.`)
              );
            }
          }

          // (c) Track title match if available
          if (!matchedFile && trackInfo?.title) {
            const safeTitle = trackInfo.title.toLowerCase().replace(/[^a-z0-9]/g, '_');
            matchedFile = validFiles.find((f) => f.name.toLowerCase().includes(safeTitle));
          }

          // (d) Single track release fallback
          if (!matchedFile && (trackInfo?.totalTracks === 1 || validFiles.length === 1)) {
            matchedFile = validFiles.find((f) => /\.(wav|mp3|flac|aac|m4a|ogg)$/i.test(f.name)) || validFiles[0];
          }

          // (e) Multi-track index fallback
          if (!matchedFile && trackInfo?.trackIndex !== undefined) {
            matchedFile = validFiles[trackInfo.trackIndex] || validFiles[0];
          }

          if (!matchedFile) {
            matchedFile = validFiles[0];
          }
        } else if (folderType === 'cover') {
          // (a) Image files with "cover", "artwork", or "3000" in filename
          matchedFile = validFiles.find(
            (f) =>
              /\.(jpg|jpeg|png|webp|avif)$/i.test(f.name) &&
              /(cover|artwork|art|3000)/i.test(f.name)
          );

          // (b) Any image file
          if (!matchedFile) {
            matchedFile = validFiles.find((f) => /\.(jpg|jpeg|png|webp|avif)$/i.test(f.name));
          }

          // (c) First available file
          if (!matchedFile) {
            matchedFile = validFiles[0];
          }
        } else {
          // AI declarations or supporting documents
          matchedFile = validFiles[0];
        }

        if (matchedFile && matchedFile.name) {
          const discoveredPath = `${folderPath}/${matchedFile.name}`;
          const res = await getAdminSignedFileUrl(folderBucket, discoveredPath, expiresIn);

          let resolvedUrl = res.url;

          // If signed URL failed, attempt download fallback
          if (!resolvedUrl) {
            try {
              const { data: dlBlob } = await supabase.storage.from(folderBucket).download(discoveredPath);
              if (dlBlob) {
                resolvedUrl = URL.createObjectURL(dlBlob);
              }
            } catch (dlErr) {}
          }

          if (resolvedUrl) {
            // Auto-heal database record pointing to old path, folder, or escaped string
            if (typeof autoRepairCallback === 'function') {
              try {
                await autoRepairCallback(discoveredPath);
              } catch (repairErr) {
                console.warn('[adminService] Auto-repair callback error (listing):', repairErr.message);
              }
            }

            console.log('[SenritsuX Storage Resolution]', {
              submission_id: debugContext?.submission_id || 'N/A',
              user_id: debugContext?.user_id || 'N/A',
              track_id: debugContext?.track_id || 'N/A',
              bucket: folderBucket,
              requested_storage_path: requestedPath,
              resolved_storage_path: discoveredPath,
              storage_object_exists: true,
              was_repaired: true,
            });

            return {
              exists: true,
              path: discoveredPath,
              bucket: folderBucket,
              url: resolvedUrl,
              errorType: null,
              wasRepaired: true,
            };
          } else if (res.error) {
            lastStorageError = res.error;
          }
        }
      }
    } catch (folderErr) {
      lastStorageError = folderErr;
      console.warn(`[adminService] Folder inspection error for ${folderPath}:`, folderErr);
    }
  }
}

  // Section 3: Section 12 Debug log for failure with exact error reported
  console.log('[SenritsuX Storage Resolution Failed]', {
    submission_id: debugContext?.submission_id || 'N/A',
    user_id: debugContext?.user_id || 'N/A',
    track_id: debugContext?.track_id || 'N/A',
    bucket,
    requested_storage_path: requestedPath,
    resolved_storage_path: null,
    storage_object_exists: false,
    exact_storage_error: lastStorageError?.message || lastStorageError || 'Object not found in candidate paths or folders',
    status_code: lastStorageError?.statusCode || lastStorageError?.status || null,
  });

  // 4. Precise Error State Classification reporting exact Storage API error
  if (hadUnauthorizedError) {
    return {
      exists: false,
      path: requestedPath !== 'N/A' ? requestedPath : null,
      bucket,
      url: null,
      errorType: 'unauthorized',
      message: lastStorageError?.message
        ? `Storage permission error (${lastStorageError.statusCode || 403}): ${lastStorageError.message}`
        : 'Unable to access audio file (Unauthorized).',
      rawError: lastStorageError,
    };
  }

  if (!hasAnyNonEmptyCandidate && foldersToInspect.size === 0) {
    return {
      exists: false,
      path: null,
      bucket,
      url: null,
      errorType: 'missing',
      message: 'No storage path or candidate folder provided.',
      rawError: null,
    };
  }

  // Section 11: Exact Storage API 404 error
  const errMsg = lastStorageError?.message
    ? `Storage error: ${lastStorageError.message} (${lastStorageError.statusCode || 404})`
    : 'Storage error: Object not found (HTTP 404)';

  return {
    exists: false,
    path: requestedPath !== 'N/A' ? requestedPath : null,
    bucket,
    url: null,
    errorType: 'missing',
    message: errMsg,
    rawError: lastStorageError,
  };
}

/**
 * Repair a track's audio_processed_path in release_tracks
 * @param {string} trackId
 * @param {string} correctPath
 * @param {string} [adminId]
 * @param {string} [originalFilename]
 */
export async function repairTrackAudioPath(trackId, correctPath, adminId, originalFilename = null) {
  if (!trackId || !correctPath) return;
  try {
    const updatePayload = {
      audio_processed_path: correctPath,
      updated_at: new Date().toISOString(),
    };
    if (originalFilename && typeof originalFilename === 'string') {
      const cleanOrig = originalFilename.replace(/\\([_.\-~ ])/g, '$1');
      updatePayload.audio_original_path = cleanOrig;
    }

    await supabase
      .from('release_tracks')
      .update(updatePayload)
      .eq('id', trackId);

    if (adminId) {
      await supabase.from('admin_audit_logs').insert({
        admin_id: adminId,
        action: 'repair_track_audio_path',
        target_type: 'track',
        target_id: trackId,
        new_value: updatePayload,
        reason: 'Auto-repaired missing, escaped, or mismatched storage path for track',
      });
    }
  } catch (err) {
    console.warn('[adminService] repairTrackAudioPath error:', err);
  }
}

/**
 * Repair a release's cover_processed_path in releases
 * @param {string} releaseId
 * @param {string} correctPath
 * @param {string} [adminId]
 */
export async function repairReleaseCoverPath(releaseId, correctPath, adminId) {
  if (!releaseId || !correctPath) return;
  try {
    await supabase
      .from('releases')
      .update({
        cover_processed_path: correctPath,
        cover_url: correctPath,
        updated_at: new Date().toISOString(),
      })
      .eq('id', releaseId);

    if (adminId) {
      await supabase.from('admin_audit_logs').insert({
        admin_id: adminId,
        action: 'repair_release_cover_path',
        target_type: 'release',
        target_id: releaseId,
        new_value: { cover_processed_path: correctPath },
        reason: 'Auto-repaired missing or mismatched storage path for cover artwork',
      });
    }
  } catch (err) {
    console.warn('[adminService] repairReleaseCoverPath error:', err);
  }
}

/**
 * Securely trigger native file download from a signed URL with the specified filename.
 * @param {string} signedUrl
 * @param {string} filename
 */
export async function downloadAdminFile(signedUrl, filename) {
  if (!signedUrl) return;
  try {
    const res = await fetch(signedUrl);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
      if (document.body.contains(a)) document.body.removeChild(a);
    }, 200);
  } catch (err) {
    console.warn('[adminService] Direct fetch blob download failed, falling back to anchor navigation:', err);
    const a = document.createElement('a');
    a.href = signedUrl;
    a.download = filename;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      if (document.body.contains(a)) document.body.removeChild(a);
    }, 200);
  }
}


/**
 * Fetch all distribution submissions with complete release, tracks, credits, and artist profile data.
 * @param {string} statusFilter - filter by status, or '' for all
 * @param {number} page - 1-indexed page
 * @param {string} search - search by artist name, stage name, or release title
 */
export async function fetchAllSubmissions(statusFilter = '', page = 1, search = '') {
  try {
    let query = supabase
      .from('distribution_submissions')
      .select(
        `
        id,
        status,
        reference_code,
        notes,
        submitted_at,
        created_at,
        updated_at,
        user_id,
        release_id,
        releases (
          id,
          title,
          release_type,
          genre,
          mood,
          price_tier,
          c_line,
          c_year,
          p_line,
          p_year,
          isrc,
          senritsux_registration_number,
          primary_artists,
          featuring_artists,
          release_date,
          consumer_release_date,
          original_release_date,
          release_time,
          release_timezone,
          cover_url,
          cover_processed_path,
          status,
          created_at,
          submitted_at,
          release_social_monetisation (
            instagram_music,
            tiktok,
            youtube
          ),
          release_tracks (
            id,
            track_number,
            title,
            version_type,
            genre,
            mood,
            language,
            isrc,
            explicit,
            vocal_type,
            country_recorded,
            recording_year,
            has_lyrics,
            lyrics,
            original_release_date,
            audio_original_path,
            audio_processed_path,
            audio_format,
            audio_sample_rate,
            audio_bit_depth,
            duration_seconds,
            track_credits (
              id,
              name,
              role,
              credit_type
            ),
            track_samples (
              id,
              sampled_track_url,
              license_url
            ),
            track_ai_declarations (
              id,
              declaration_file_path,
              file_name,
              file_size,
              mime_type
            )
          )
        ),
        profiles (
          id,
          name,
          stage_name,
          email,
          avatar_url,
          city,
          genre,
          legal_name,
          is_verified
        )
      `,
        { count: 'exact' }
      )
      .order('submitted_at', { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (statusFilter) {
      query = query.eq('status', statusFilter);
    }

    const { data, error, count } = await query;
    return { data: data ?? [], count: count ?? 0, error };
  } catch (err) {
    console.error('[adminService] fetchAllSubmissions error:', err);
    return { data: [], count: 0, error: err };
  }
}

/**
 * Update the status of a distribution submission.
 * Writes an audit log entry.
 *
 * @param {string} submissionId
 * @param {string} newStatus - one of the allowed status values
 * @param {string} notes - admin notes / reason for status change
 * @param {string} adminId
 * @param {string} oldStatus - the previous status (for audit log)
 */
export async function updateSubmissionStatus(submissionId, newStatus, notes, adminId, oldStatus) {
  try {
    const updatePayload = {
      status: newStatus,
      updated_at: new Date().toISOString(),
    };
    if (notes !== undefined) updatePayload.notes = notes;

    const { data, error } = await supabase
      .from('distribution_submissions')
      .update(updatePayload)
      .eq('id', submissionId)
      .select()
      .maybeSingle();

    if (error) return { data: null, error };

    // Mirror status to releases table
    if (data?.release_id) {
      await supabase
        .from('releases')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', data.release_id);
    }

    // Write audit log
    await supabase.from('admin_audit_logs').insert({
      admin_id: adminId,
      action: `submission_status_${newStatus}`,
      target_type: 'submission',
      target_id: submissionId,
      old_value: { status: oldStatus },
      new_value: { status: newStatus, notes },
      reason: notes || null,
    });

    return { data, error: null };
  } catch (err) {
    console.error('[adminService] updateSubmissionStatus error:', err);
    return { data: null, error: err };
  }
}

/**
 * Enter or update the ISRC for a release.
 * Also updates the first track's ISRC if it's a single.
 *
 * @param {string} releaseId
 * @param {string} isrc - ISRC code (e.g. "INXYZ2600001")
 * @param {string} adminId
 * @param {string} oldIsrc - previous value for audit trail
 */
export async function updateReleaseIsrc(releaseId, isrc, adminId, oldIsrc = null) {
  try {
    const { data, error } = await supabase
      .from('releases')
      .update({ isrc, updated_at: new Date().toISOString() })
      .eq('id', releaseId)
      .select()
      .maybeSingle();

    if (error) return { data: null, error };

    await supabase.from('admin_audit_logs').insert({
      admin_id: adminId,
      action: 'release_isrc_entered',
      target_type: 'release',
      target_id: releaseId,
      old_value: { isrc: oldIsrc },
      new_value: { isrc },
      reason: 'ISRC assigned during delivery',
    });

    return { data, error: null };
  } catch (err) {
    console.error('[adminService] updateReleaseIsrc error:', err);
    return { data: null, error: err };
  }
}

/**
 * Enter or update the ISRC for an individual track.
 *
 * @param {string} trackId - release_tracks.id
 * @param {string} isrc
 * @param {string} adminId
 * @param {string} oldIsrc
 */
export async function updateTrackIsrc(trackId, isrc, adminId, oldIsrc = null) {
  try {
    const { data, error } = await supabase
      .from('release_tracks')
      .update({ isrc, updated_at: new Date().toISOString() })
      .eq('id', trackId)
      .select()
      .maybeSingle();

    if (error) return { data: null, error };

    await supabase.from('admin_audit_logs').insert({
      admin_id: adminId,
      action: 'track_isrc_entered',
      target_type: 'track',
      target_id: trackId,
      old_value: { isrc: oldIsrc },
      new_value: { isrc },
      reason: 'Track ISRC assigned during delivery',
    });

    return { data, error: null };
  } catch (err) {
    console.error('[adminService] updateTrackIsrc error:', err);
    return { data: null, error: err };
  }
}


// ============================================================
// NOTIFICATIONS
// ============================================================

/**
 * Send a notification to a specific user or broadcast to all users.
 *
 * @param {string|null} userId - target user's profiles.id, or null for broadcast
 * @param {string} title - notification title
 * @param {string} body - notification body / message
 * @param {string} adminId - acting admin's profiles.id
 */
export async function sendAdminNotification(userId, title, body, adminId) {
  try {
    if (userId) {
      // Single-user notification
      const { error } = await supabase.from('notifications').insert({
        user_id: userId,
        title,
        message: body,
        type: 'system',
        is_read: false,
        created_at: new Date().toISOString(),
      });

      if (error) return { error };
    } else {
      // Broadcast: fetch all profile IDs and insert in batch
      const { data: profiles, error: fetchError } = await supabase
        .from('profiles')
        .select('id');

      if (fetchError) return { error: fetchError };

      const insertRows = (profiles ?? []).map((p) => ({
        user_id: p.id,
        title,
        message: body,
        type: 'system',
        is_read: false,
        created_at: new Date().toISOString(),
      }));

      if (insertRows.length > 0) {
        const { error } = await supabase.from('notifications').insert(insertRows);
        if (error) return { error };
      }
    }

    // Write audit log
    await supabase.from('admin_audit_logs').insert({
      admin_id: adminId,
      action: userId ? 'notification_sent_single' : 'notification_broadcast',
      target_type: 'notification',
      target_id: userId || null,
      new_value: { title, body },
      reason: 'Admin notification',
    });

    return { error: null };
  } catch (err) {
    console.error('[adminService] sendAdminNotification error:', err);
    return { error: err };
  }
}


// ============================================================
// AUDIT LOG
// ============================================================

/**
 * Fetch audit log entries with optional filters.
 *
 * @param {object} filters - { action, adminId, fromDate, toDate }
 * @param {number} page - 1-indexed
 */
export async function fetchAuditLogs(filters = {}, page = 1) {
  try {
    let query = supabase
      .from('admin_audit_logs')
      .select(
        `
        id,
        action,
        target_type,
        target_id,
        old_value,
        new_value,
        reason,
        created_at,
        admin_id,
        profiles (
          id,
          name,
          stage_name,
          email
        )
      `,
        { count: 'exact' }
      )
      .order('created_at', { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (filters.action) query = query.eq('action', filters.action);
    if (filters.adminId) query = query.eq('admin_id', filters.adminId);
    if (filters.fromDate) query = query.gte('created_at', filters.fromDate);
    if (filters.toDate) query = query.lte('created_at', filters.toDate);

    const { data, error, count } = await query;
    return { data: data ?? [], count: count ?? 0, error };
  } catch (err) {
    console.error('[adminService] fetchAuditLogs error:', err);
    return { data: [], count: 0, error: err };
  }
}

// ─────────────────────────────────────────────────────────────
// RELEASE STORAGE ARCHITECTURE REBUILD FUNCTIONS
// ─────────────────────────────────────────────────────────────

/**
 * Fetch release storage index records with dynamic filtering and pagination.
 * Supports searching by: SenritsuX Registration Number, Release Title, or User ID.
 * @param {object} options
 * @param {string} [options.search]
 * @param {string} [options.statusFilter]
 * @param {number} [options.page=1]
 * @param {number} [options.limit=20]
 * @returns {Promise<{ data: Array, count: number, error: any }>}
 */
export async function fetchReleaseStorageIndex({ search = '', statusFilter = '', page = 1, limit = 20 } = {}) {
  try {
    let query = supabase
      .from('release_storage_index')
      .select('*, releases(id, title, genre, release_type, release_date, status, cover_url, cover_processed_path, profiles(id, name, stage_name, email, avatar_url))', { count: 'exact' });

    if (statusFilter) {
      query = query.or(`audio_status.eq.${statusFilter},cover_status.eq.${statusFilter}`);
    }

    if (search && search.trim()) {
      const q = search.trim();
      query = query.or(`registration_number.ilike.%${q}%,release_title.ilike.%${q}%,user_id.eq.${q}`);
    }

    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query.order('submitted_at', { ascending: false }).range(from, to);

    const { data, count, error } = await query;
    if (error) {
      console.warn('[adminService] fetchReleaseStorageIndex notice:', error.message);
      return { data: [], count: 0, error };
    }
    return { data: data || [], count: count || 0, error: null };
  } catch (err) {
    console.error('[adminService] fetchReleaseStorageIndex exception:', err);
    return { data: [], count: 0, error: err };
  }
}

/**
 * Check real-time storage health for a release in 'tracks' and 'covers' buckets.
 * @param {string} registrationNumber - e.g. 'SX-2026-0042'
 * @returns {Promise<{ registrationNumber: string, audio: object, cover: object }|null>}
 */
export async function fetchReleaseStorageHealth(registrationNumber) {
  if (!registrationNumber) return null;
  try {
    // 1. Check 'tracks' bucket
    const { data: trackFiles } = await supabase.storage
      .from('tracks')
      .list(registrationNumber, { limit: 100 });
    const validTrackFiles = (trackFiles || []).filter(
      (f) => f.name && !f.name.startsWith('.') && !f.name.endsWith('/')
    );

    // 2. Check 'covers' bucket
    const { data: coverFiles } = await supabase.storage
      .from('covers')
      .list(registrationNumber, { limit: 20 });
    const validCoverFiles = (coverFiles || []).filter(
      (f) => f.name && !f.name.startsWith('.') && !f.name.endsWith('/')
    );

    // 3. Check DB records
    const { data: dbTracks } = await supabase
      .from('release_tracks')
      .select('id, track_number, title, storage_path, audio_processed_path')
      .eq('senritsux_registration_number', registrationNumber);

    const totalExpected = dbTracks?.length || (validTrackFiles.length > 0 ? validTrackFiles.length : 1);
    const uploadedTracks = validTrackFiles.length;
    const uploadedCovers = validCoverFiles.length;

    const audioOk = uploadedTracks >= totalExpected && totalExpected > 0;
    const coverOk = uploadedCovers >= 1;

    return {
      registrationNumber,
      audio: {
        uploaded: uploadedTracks,
        total: totalExpected,
        ok: audioOk,
        label: `${uploadedTracks} / ${totalExpected} ${audioOk ? '✓' : '⚠'}`,
      },
      cover: {
        uploaded: uploadedCovers,
        total: 1,
        ok: coverOk,
        label: `${uploadedCovers} / 1 ${coverOk ? '✓' : '⚠'}`,
      },
    };
  } catch (err) {
    console.warn('[adminService] fetchReleaseStorageHealth error:', err.message);
    return null;
  }
}

/**
 * Non-destructively migrates existing legacy release files from 'audio' bucket
 * to new 'tracks' and 'covers' buckets under {registration_number}/...
 * Originals in 'audio' are strictly preserved.
 * @param {string} releaseId
 * @param {string} [adminId]
 * @returns {Promise<{ success: boolean, registrationNumber?: string, error?: string }>}
 */
export async function migrateReleaseToNewStorage(releaseId, adminId) {
  if (!releaseId) return { success: false, error: 'Missing releaseId' };

  try {
    const { data: rel, error: relErr } = await supabase
      .from('releases')
      .select('*, release_tracks(*)')
      .eq('id', releaseId)
      .single();

    if (relErr || !rel) {
      return { success: false, error: relErr?.message || 'Release not found' };
    }

    const regNumber = rel.senritsux_registration_number;
    if (!regNumber) {
      return { success: false, error: 'Release missing SenritsuX Registration Number' };
    }

    // 1. Migrate Cover Artwork
    let newCoverPath = rel.cover_storage_path;
    if (!newCoverPath || !newCoverPath.startsWith(`${regNumber}/`)) {
      const oldCover = rel.cover_processed_path || rel.cover_url;
      if (oldCover) {
        const cleanedOld = cleanStoragePath('audio', oldCover);
        if (cleanedOld?.path) {
          const targetCoverPath = `${regNumber}/cover.jpg`;
          try {
            const { error: copyErr } = await supabase.storage
              .from('audio')
              .copy(cleanedOld.path, targetCoverPath, { destinationBucket: 'covers' });

            if (!copyErr) {
              newCoverPath = targetCoverPath;
            }
          } catch {}
        }
      }
    }

    // 2. Migrate Audio Tracks
    const migratedTracks = [];
    for (const trk of rel.release_tracks || []) {
      let newTrackPath = trk.storage_path;
      if (!newTrackPath || !newTrackPath.startsWith(`${regNumber}/`)) {
        const oldAudio = trk.audio_processed_path || trk.audio_original_path;
        if (oldAudio) {
          const cleanedOld = cleanStoragePath('audio', oldAudio);
          if (cleanedOld?.path) {
            const numStr = (trk.track_number || 1).toString().padStart(2, '0');
            const safeTitle = (trk.title || 'track').replace(/[^a-zA-Z0-9._-]/g, '_');
            const targetTrackPath = `${regNumber}/${numStr}_${safeTitle}.wav`;
            try {
              const { error: copyErr } = await supabase.storage
                .from('audio')
                .copy(cleanedOld.path, targetTrackPath, { destinationBucket: 'tracks' });

              if (!copyErr) {
                newTrackPath = targetTrackPath;
                await supabase
                  .from('release_tracks')
                  .update({
                    senritsux_registration_number: regNumber,
                    storage_bucket: 'tracks',
                    storage_path: newTrackPath,
                    updated_at: new Date().toISOString(),
                  })
                  .eq('id', trk.id);
              }
            } catch {}
          }
        }
      }
      migratedTracks.push({ id: trk.id, path: newTrackPath });
    }

    // 3. Update Releases Table
    if (newCoverPath) {
      await supabase
        .from('releases')
        .update({
          cover_bucket: 'covers',
          cover_storage_path: newCoverPath,
          updated_at: new Date().toISOString(),
        })
        .eq('id', releaseId);
    }

    // 4. Update release_storage_index Table
    const allTracksOk = migratedTracks.length > 0 && migratedTracks.every((t) => t.path);
    await supabase.from('release_storage_index').upsert({
      registration_number: regNumber,
      user_id: rel.user_id,
      release_id: releaseId,
      release_title: rel.title,
      release_type: rel.release_type,
      track_count: rel.release_tracks?.length || 0,
      audio_bucket: 'tracks',
      cover_bucket: 'covers',
      audio_status: allTracksOk ? 'verified' : 'uploaded',
      cover_status: newCoverPath ? 'verified' : 'uploaded',
      updated_at: new Date().toISOString(),
    }, { onConflict: 'registration_number' });

    if (adminId) {
      await supabase.from('admin_audit_logs').insert({
        admin_id: adminId,
        action: 'migrate_release_to_new_storage',
        target_type: 'release',
        target_id: releaseId,
        new_value: { registration_number: regNumber, cover_path: newCoverPath, tracks: migratedTracks },
        reason: 'Migrated legacy release assets to tracks and covers buckets',
      });
    }

    return { success: true, registrationNumber: regNumber, coverPath: newCoverPath, tracks: migratedTracks };
  } catch (err) {
    console.error('[adminService] migrateReleaseToNewStorage error:', err);
    return { success: false, error: err.message };
  }
}

// ============================================================
// CONTACT MESSAGES
// ============================================================

/**
 * Fetch contact messages with pagination, filtering, and search.
 */
export async function fetchContactMessages({ page = 1, pageSize = PAGE_SIZE, status = 'all', search = '' } = {}) {
  try {
    // Filter messages: automatically retain and display only messages younger than 30 days
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    let query = supabase
      .from('contact_messages')
      .select('*', { count: 'exact' })
      .gte('created_at', thirtyDaysAgo);

    if (status && status !== 'all') {
      query = query.eq('status', status);
    }

    if (search && search.trim()) {
      const q = search.trim();
      query = query.or(`name.ilike.%${q}%,email.ilike.%${q}%,subject.ilike.%${q}%,message.ilike.%${q}%`);
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, count, error } = await query
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) throw error;

    // Temporary development diagnostic
    console.log(`[Admin Contact Messages] Query returned: ${count || 0} records (page size: ${data?.length || 0})`);

    // Attach registered profiles if available
    const userIds = [...new Set((data || []).map((m) => m.user_id).filter(Boolean))];
    let profilesMap = {};
    if (userIds.length > 0) {
      try {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, name, stage_name, email, avatar_url')
          .in('id', userIds);
        if (profilesData) {
          profilesMap = Object.fromEntries(profilesData.map((p) => [p.id, p]));
        }
      } catch (profErr) {
        console.warn('[adminService] Could not resolve profiles for messages:', profErr.message);
      }
    }

    const enriched = (data || []).map((m) => ({
      ...m,
      profiles: m.user_id ? profilesMap[m.user_id] || null : null,
    }));

    return {
      data: enriched,
      count: count || 0,
      page,
      pageSize,
      error: null,
    };
  } catch (err) {
    console.warn('[adminService] Direct contact_messages query failed, attempting /api/contact/messages fallback...', err.message);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers = { 'Content-Type': 'application/json' };
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        status,
        search: search || '',
      });
      const res = await fetch(`/api/contact/messages?${params.toString()}`, { headers });
      if (res.ok) {
        const json = await res.json();
        console.log(`[Admin Contact Messages] API fallback returned: ${json.count || 0} records`);
        return {
          data: json.data || [],
          count: json.count || 0,
          page,
          pageSize,
          error: null,
        };
      }
    } catch (fallbackErr) {
      console.error('[adminService] Fallback to /api/contact/messages failed:', fallbackErr);
    }
    return { data: [], count: 0, page, pageSize, error: err };
  }
}

/**
 * Update contact message status ('unread', 'read', 'replied', 'archived') and optional admin notes.
 */
export async function updateContactMessageStatus(id, newStatus, adminNotes = null, adminId = null) {
  try {
    const updatePayload = {
      status: newStatus,
      updated_at: new Date().toISOString(),
    };
    if (adminNotes !== null) {
      updatePayload.admin_notes = adminNotes;
    }

    const { data, error } = await supabase
      .from('contact_messages')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    if (adminId) {
      await supabase.from('admin_audit_logs').insert({
        admin_id: adminId,
        action: 'update_contact_message_status',
        target_type: 'contact_message',
        target_id: id,
        new_value: { status: newStatus, admin_notes: adminNotes },
        reason: `Updated contact message status to ${newStatus}`,
      });
    }

    return { data, error: null };
  } catch (err) {
    console.error('[adminService] updateContactMessageStatus error:', err);
    return { data: null, error: err };
  }
}

/**
 * Fetch count of unread contact messages for badge notifications.
 */
export async function fetchUnreadContactCount() {
  try {
    const { count, error } = await supabase
      .from('contact_messages')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'unread');

    if (error) throw error;
    return { count: count || 0, error: null };
  } catch (err) {
    return { count: 0, error: err };
  }
}

/**
 * Fetch all OAC requests with artist profile and release joins for admin review.
 */
export async function fetchAdminOacRequests({
  page = 1,
  pageSize = PAGE_SIZE,
  status = 'all',
  search = '',
} = {}) {
  try {
    let query = supabase
      .from('oac_requests')
      .select(`
        id,
        user_id,
        artist_profile_id,
        release_id,
        youtube_release_url,
        youtube_channel_url,
        status,
        review_notes,
        rejection_reason,
        submitted_at,
        updated_at,
        reviewed_at,
        reviewed_by,
        profiles (
          id,
          name,
          stage_name,
          email,
          avatar_url,
          instagram,
          city,
          genre,
          secondary_genre
        ),
        releases (
          id,
          title,
          release_type,
          genre,
          status,
          senritsux_registration_number,
          cover_url,
          release_date,
          created_at
        )
      `, { count: 'exact' });

    if (status && status !== 'all') {
      query = query.eq('status', status);
    }

    query = query.order('submitted_at', { ascending: false });

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, count, error } = await query;

    if (error) {
      if (error.code === '42P01') {
        return { data: [], total: 0, page, pageSize, totalPages: 0, error: null };
      }
      throw error;
    }

    let filtered = data || [];
    if (search && search.trim()) {
      const q = search.toLowerCase().trim();
      filtered = filtered.filter((r) => {
        const stageName = (r.profiles?.stage_name || '').toLowerCase();
        const name = (r.profiles?.name || '').toLowerCase();
        const email = (r.profiles?.email || '').toLowerCase();
        const releaseTitle = (r.releases?.title || '').toLowerCase();
        const regNum = (r.releases?.senritsux_registration_number || '').toLowerCase();
        return (
          stageName.includes(q) ||
          name.includes(q) ||
          email.includes(q) ||
          releaseTitle.includes(q) ||
          regNum.includes(q)
        );
      });
    }

    return {
      data: filtered,
      total: count || filtered.length,
      page,
      pageSize,
      totalPages: Math.ceil((count || filtered.length) / pageSize),
      error: null,
    };
  } catch (err) {
    console.error('[adminService] fetchAdminOacRequests error:', err);
    return { data: [], total: 0, page, pageSize, totalPages: 0, error: err };
  }
}

/**
 * Update the status of an OAC request (review, approve, reject) and write audit log.
 */
export async function updateOacRequestStatus({
  requestId,
  status,
  reviewNotes = '',
  rejectionReason = '',
  adminId = null,
}) {
  try {
    // 1. Fetch existing request for old_value
    const { data: oldReq } = await supabase
      .from('oac_requests')
      .select('id, status, user_id, release_id, review_notes, rejection_reason')
      .eq('id', requestId)
      .maybeSingle();

    const updates = {
      status,
      review_notes: reviewNotes || null,
      rejection_reason: rejectionReason || null,
      reviewed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    if (adminId) {
      updates.reviewed_by = adminId;
    }

    const { data, error } = await supabase
      .from('oac_requests')
      .update(updates)
      .eq('id', requestId)
      .select()
      .single();

    if (error) throw error;

    // 2. Audit log entry
    if (adminId) {
      await supabase.from('admin_audit_logs').insert({
        admin_id: adminId,
        action: 'oac_request_review',
        target_type: 'oac_request',
        target_id: requestId,
        old_value: oldReq || {},
        new_value: { status, review_notes: reviewNotes, rejection_reason: rejectionReason },
        reason: `Updated OAC request status to ${status}`,
      });
    }

    // 3. Send notification to artist
    if (oldReq?.user_id) {
      const isApproved = status === 'approved';
      const isRejected = status === 'rejected';
      let title = 'OAC Request Update';
      let message = `Your Official Artist Channel request is now ${status}.`;

      if (isApproved) {
        title = 'OAC Request Approved & Processed';
        message = 'Your Official Artist Channel request has been approved by the SenritsuX review team and submitted into the distribution partner pipeline for YouTube OAC processing.';
      } else if (isRejected) {
        title = 'OAC Request Needs Attention';
        message = rejectionReason
          ? `Your Official Artist Channel request was not approved: ${rejectionReason}`
          : 'Your Official Artist Channel request was reviewed and could not be approved at this time. Please check your eligibility and channel criteria.';
      }

      await supabase.from('notifications').insert({
        user_id: oldReq.user_id,
        title,
        message,
        type: 'distribution',
        data: {
          oac_request_id: requestId,
          status,
          rejection_reason: rejectionReason,
        },
      });
    }

    return { data, error: null };
  } catch (err) {
    console.error('[adminService] updateOacRequestStatus error:', err);
    return { data: null, error: err };
  }
}

/**
 * Fetch count of pending OAC requests for admin badge indicator.
 */
export async function fetchPendingOacCount() {
  try {
    const { count, error } = await supabase
      .from('oac_requests')
      .select('id', { count: 'exact', head: true })
      .in('status', ['pending', 'under_review']);

    if (error) {
      if (error.code === '42P01') return { count: 0, error: null };
      throw error;
    }
    return { count: count || 0, error: null };
  } catch (err) {
    return { count: 0, error: err };
  }
}

